import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col } from "react-bootstrap";
import moment from "moment";

import * as validator from "../../../../helpers/validator";
import { trimFieldsData } from "../../../../utils";
import { ButtonView, Input, Select, TimePicker } from "../../../controls";

const BookRoomTab = ({
  roomList,
  userList,
  user,
  bookRoom,
  bookPending,
  isModal = false,
  onClose,
  submitRef,
}) => {
  const [formData, setFormData] = useState({
    roomId: "",
    employeeId: user ? user._id : "",
    purpose: "",
    date: new Date(),
    time: moment().add(1, "minute").format("HH:mm"),
    checkOutTime: moment().add(1, "minute").add(1, "hour").format("HH:mm"),
    occupancyType: "PRIVATE",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user && !formData.employeeId) {
      setFormData((prev) => ({ ...prev, employeeId: user._id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const roomOptions = roomList.map((room) => ({
    value: room._id,
    label: `${room.name} (${room.location && typeof room.location === "object" ? (room.location.name || "") : (room.location || "")})`,
  }));

  const filteredUserList = userList.filter((emp) => user && emp._id === user._id);

  const employeeOptions = filteredUserList.map((emp) => ({
    value: emp._id,
    label: emp.empNumber
      ? `${emp.firstName} ${emp.lastName} (${emp.empNumber})`
      : `${emp.firstName} ${emp.lastName}`,
  }));

  const occupancyTypeOptions = [
    { value: "PRIVATE", label: "Private" },
    { value: "SHARE", label: "Shared" },
  ];

  const handleSelectChange = (field) => (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, date }));
  };

  const handleTimeChange = (timeMoment) => {
    setFormData((prev) => ({
      ...prev,
      time: timeMoment ? timeMoment.format("HH:mm") : "",
    }));
  };

  const handleCheckOutTimeChange = (timeMoment) => {
    setFormData((prev) => ({
      ...prev,
      checkOutTime: timeMoment ? timeMoment.format("HH:mm") : "",
    }));
  };

  const handleSubmit = () => {
    const rules = {
      required: ["roomId", "employeeId", "purpose", "date", "time", "checkOutTime", "occupancyType"],
    };

    const { errors: validationErrors } = validator.validate(rules, {
      ...trimFieldsData(formData),
    });

    setErrors(validationErrors);

    if (!Object.keys(validationErrors).length) {
      const checkInTime = moment(
        `${moment(formData.date).format("YYYY-MM-DD")} ${formData.time}`,
        "YYYY-MM-DD HH:mm"
      );

      const checkOutTime = moment(
        `${moment(formData.date).format("YYYY-MM-DD")} ${formData.checkOutTime}`,
        "YYYY-MM-DD HH:mm"
      );

      if (checkOutTime.isSameOrBefore(checkInTime)) {
        setErrors({ checkOutTime: "Check-out time must be after check-in time." });
        return;
      }

      bookRoom({
        roomId: formData.roomId,
        employeeId: formData.employeeId,
        purpose: formData.purpose,
        checkInTime: checkInTime.toISOString(),
        checkOutTime: checkOutTime.toISOString(),
        occupancyType: formData.occupancyType,
      });

      // Reset form (except employeeId and occupancyType)
      setFormData((prev) => ({
        ...prev,
        roomId: "",
        purpose: "",
        date: new Date(),
        time: moment().add(1, "minute").format("HH:mm"),
        checkOutTime: moment().add(1, "minute").add(1, "hour").format("HH:mm"),
      }));

      if (onClose) {
        onClose();
      }
    }
  };

  // Assign to ref synchronously during render — no useEffect, no setState loop
  if (isModal && submitRef) {
    submitRef.current = handleSubmit;
  }

  const selectedRoom = roomOptions.find((o) => o.value === formData.roomId) || null;
  const selectedEmployee = employeeOptions.find((o) => o.value === formData.employeeId) || null;
  const selectedOccupancyType = occupancyTypeOptions.find((o) => o.value === formData.occupancyType) || null;

  // The logged-in user can only book for themselves, so disable the select universally
  const isEmployeeSelectDisabled = true;

  const renderFormContent = () => (
    <div className="p-3">
      <Row className="mb-2">
        <Col md={6} className="mb-3">
          <Select
            label="Select Room"
            value={selectedRoom}
            options={roomOptions}
            onChange={handleSelectChange("roomId")}
            isrequired={true}
            error={errors["roomId"]}
            placeholder="Select Room"
          />
        </Col>
        <Col md={6} className="mb-3">
          <Select
            label="Booked For Employee"
            value={selectedEmployee}
            options={employeeOptions}
            onChange={handleSelectChange("employeeId")}
            isrequired={true}
            disabled={isEmployeeSelectDisabled}
            error={errors["employeeId"]}
          />
        </Col>
        <Col md={6} className="mb-3">
          <Input
            name="bookingDate"
            label="Booking Date"
            value={moment(formData.date).format("YYYY-MM-DD")}
            type="date"
            min={moment().format("YYYY-MM-DD")}
            onChange={(e) => handleDateChange(e.target.value ? new Date(e.target.value) : new Date())}
            size="fluid"
            isrequired={true}
            error={errors["date"]}
          />
        </Col>
        <Col md={6} className="mb-3 mt-3 mt-md-0">
          <TimePicker
            label="Check-In Time"
            onChange={handleTimeChange}
            isrequired={true}
            error={errors["time"]}
            placeholder={formData.time ? `${formData.time}` : "Select time"}
          />
        </Col>
        <Col md={6} className="mb-3">
          <TimePicker
            label="Check-Out Time"
            onChange={handleCheckOutTimeChange}
            isrequired={true}
            error={errors["checkOutTime"]}
            placeholder={formData.checkOutTime ? `${formData.checkOutTime}` : "Select time"}
          />
        </Col>
        <Col md={6} className="mb-3">
          <Select
            label="Occupancy Type"
            value={selectedOccupancyType}
            options={occupancyTypeOptions}
            onChange={handleSelectChange("occupancyType")}
            isrequired={true}
            error={errors["occupancyType"]}
          />
        </Col>
        <Col md={6} className="mb-3">
          <Input
            name="purpose"
            label="Purpose of Booking"
            value={formData.purpose}
            type="text"
            placeholder="e.g. Project Sync / Interview"
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, purpose: e.target.value }))
            }
            size="fluid"
            error={errors["purpose"]}
            isrequired={true}
          />
        </Col>
        {/* Buttons shown only in standalone (non-modal) view */}
        {!isModal && (
          <Col xs={12} className="d-flex justify-content-end mt-4">
            <ButtonView
              onClick={handleSubmit}
              title={bookPending ? "Booking..." : "Book Room"}
              styles="secondary"
              disabled={bookPending}
            />
          </Col>
        )}
      </Row>
    </div>
  );

  if (isModal) {
    return <div className="book-room-form">{renderFormContent()}</div>;
  }

  return (
    <div className="book-room-tab mt-3">
      <Card>
        <Card.Body>
          <Card.Header>
            <Card.Title className="m-0">Book a Room</Card.Title>
          </Card.Header>
          {renderFormContent()}
        </Card.Body>
      </Card>
    </div>
  );
};

BookRoomTab.propTypes = {
  roomList: PropTypes.array.isRequired,
  userList: PropTypes.array.isRequired,
  user: PropTypes.object,
  bookRoom: PropTypes.func.isRequired,
  bookPending: PropTypes.bool,
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  submitRef: PropTypes.object,
};

export default BookRoomTab;
